import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSession } from '@/stores/session';
import { BASE as COURSES_BASE } from '../api';
import { canManageLms } from '../constants';
import { useCourses } from '../queries';
import { useCoursesUi, type CoursesViewMode } from '../store/viewPrefs';
import type { Course } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type CourseOrderingColumn = 'title' | 'category' | 'is_published' | 'created';

const VIEW_MODES: readonly ViewModeOption<CoursesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

/** Published/Draft buckets in declared order for the grouped view. */
const PUBLISHED_GROUPS = [
  { key: 'published', label: 'Published', value: true },
  { key: 'draft', label: 'Draft', value: false },
] as const;

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export function CoursesListPage() {
  const navigate = useNavigate();
  const role = useSession((s) => s.role);
  const canManage = canManageLms(role);

  const view = useCoursesUi((s) => s.view);
  const setView = useCoursesUi((s) => s.setView);
  const ordering = useCoursesUi((s) => s.ordering);
  const setOrdering = useCoursesUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [published, setPublished] = useState('all');
  const [page, setPage] = useState(1);

  const isPublished = published === 'all' ? undefined : published === 'published';

  const { data, isPending, isError, error, isFetching } = useCourses({
    page,
    search: search || undefined,
    is_published: isPublished,
    ordering,
  });

  const results = data?.results ?? [];
  const open = (c: Course) => navigate(`/lms/courses/${c.public_id}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Courses</h1>
        {canManage ? (
          <Button onClick={() => navigate('/lms/courses/new')}>
            <Plus /> New course
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by title…"
              className="pl-8"
              aria-label="Search courses"
            />
          </div>
          <Select
            value={published}
            onValueChange={(v) => {
              setPage(1);
              setPublished(v);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `courses.csv` without leaving the SPA.
              void exportCsv(COURSES_BASE, {
                search: search || undefined,
                is_published: isPublished,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export courses as CSV"
            title="Export courses as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <CoursesSkeleton />
      ) : results.length === 0 ? (
        <CoursesTable
          courses={[]}
          onRowClick={() => undefined}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <CoursesTable
          courses={results}
          onRowClick={open}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <CoursesList courses={results} onOpen={open} />
      ) : view === 'cards' ? (
        <CoursesCards courses={results} onOpen={open} />
      ) : (
        <CoursesGroupedByStatus courses={results} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'course' : 'courses'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  courses: Course[];
  onOpen: (c: Course) => void;
}

interface CoursesTableProps {
  courses: Course[];
  onRowClick: (c: Course) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function PublishedBadge({ published }: { published: boolean }) {
  return (
    <Badge variant={published ? 'default' : 'secondary'}>{published ? 'Published' : 'Draft'}</Badge>
  );
}

function CoursesTable({ courses, onRowClick, ordering, onOrderingChange }: CoursesTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<CourseOrderingColumn>
              column="title"
              value={ordering}
              onChange={onOrderingChange}
            >
              Title
            </SortableTableHead>
            <SortableTableHead<CourseOrderingColumn>
              column="category"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Category
            </SortableTableHead>
            <SortableTableHead<CourseOrderingColumn>
              column="is_published"
              value={ordering}
              onChange={onOrderingChange}
            >
              Published
            </SortableTableHead>
            <SortableTableHead<CourseOrderingColumn>
              column="created"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden lg:table-cell"
            >
              Created
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {courses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No courses found.
              </TableCell>
            </TableRow>
          ) : (
            courses.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => onRowClick(c)}>
                <TableCell className="font-medium">{c.title}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {c.category || '—'}
                </TableCell>
                <TableCell>
                  <PublishedBadge published={c.is_published} />
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {formatDate(c.created)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CoursesCards({ courses, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {courses.map((c) => (
        <Card
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(c)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(c);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{c.title}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <PublishedBadge published={c.is_published} />
            {c.category ? (
              <span className="text-muted-foreground truncate">{c.category}</span>
            ) : null}
            <span className="text-muted-foreground ml-auto">{formatDate(c.created)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CoursesList({ courses, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {courses.map((c) => (
        <li key={c.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(c)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{c.title}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {c.category || '—'}
            </span>
            <PublishedBadge published={c.is_published} />
            <span className="text-muted-foreground text-xs">{formatDate(c.created)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function CoursesGroupedByStatus({ courses, onOpen }: ListProps) {
  // Bucket by publish status, preserving the declared order; skip empty groups.
  const groups = useMemo(() => {
    return PUBLISHED_GROUPS.map((g) => ({
      key: g.key,
      label: g.label,
      bucket: courses.filter((c) => c.is_published === g.value),
    }));
  }, [courses]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`courses-status-${key}`}>
            <h2
              id={`courses-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <CoursesCards courses={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function CoursesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
